/**
 * soleil-presale — pre-sale awareness gate.
 *
 * Intercepts "Add to cart" on a pre-sale product and opens an agreement
 * popup. Confirming closes the popup, runs the NATIVE add, and lets the
 * cart drawer open — the regular flow with one acknowledgement step.
 *
 * Interception point: Horizon binds its `on:submit="/handleSubmit"` via a
 * delegated listener on `document` in the CAPTURE phase (assets/component.js).
 * `window` sits ABOVE `document` in the capture path, so a window-capture
 * `submit` listener runs FIRST and can stop the event before Horizon's
 * handler ever sees it. On confirm we set a flag and re-submit, so the second
 * pass falls straight through to the native add.
 *
 * Confirm order matters: we close the <dialog> BEFORE re-submitting. The
 * cart drawer (assets/cart-drawer.js) defers its auto-open while the add
 * originates inside an open modal <dialog>; closing first means the drawer
 * opens cleanly right after the popup — "popup closes, then drawer opens".
 *
 * Fully data-gated: the block only renders on pre-sale products, so this
 * script is only present there. If it fails to load, the native ATC works
 * unchanged (product adds directly, no popup).
 */
class SoleilPresale extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId || '';
    this.dialog = this.querySelector('dialog');
    this.pendingForm = null;

    if (!this.dialog || !this.productId) return;

    // Beat Horizon's document-capture submit delegation.
    window.addEventListener('submit', this.handleSubmitCapture, true);

    this.querySelector('[data-presale-confirm]')?.addEventListener('click', this.confirm);
    this.querySelector('[data-presale-cancel]')?.addEventListener('click', this.close);
    this.querySelector('[data-presale-close]')?.addEventListener('click', this.close);
  }

  disconnectedCallback() {
    window.removeEventListener('submit', this.handleSubmitCapture, true);
  }

  /** @param {SubmitEvent} event */
  handleSubmitCapture = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Only THIS pre-sale product's add-to-cart form. Quick-add / cross-sell
    // forms for other products carry a different product id and pass through.
    const productForm = form.closest('product-form-component');
    if (!productForm || productForm.getAttribute('data-product-id') !== this.productId) return;

    // Let the express-checkout / dynamic payment button through untouched.
    const submitter = event.submitter;
    if (
      submitter instanceof Element &&
      submitter.closest('accelerated-checkout, .shopify-payment-button, [data-shopify="payment-button"]')
    ) {
      return;
    }

    // Already acknowledged → allow the native add to proceed.
    if (form.dataset.presaleConfirmed === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.pendingForm = form;
    this.open();
  };

  open() {
    if (typeof this.dialog.showModal === 'function') {
      if (!this.dialog.open) this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
  }

  close = () => {
    if (this.dialog.open) this.dialog.close();
  };

  confirm = () => {
    const form = this.pendingForm;
    this.pendingForm = null;

    // Close the popup FIRST so the cart drawer's modal-deferral clears and it
    // can auto-open right after (popup closes → drawer opens).
    this.close();

    if (!form) return;

    // Flag so the window-capture gate lets this second submit fall through to
    // Horizon's native handler. requestSubmit() dispatches synchronously, so
    // by the time it returns the native add has already been kicked off.
    form.dataset.presaleConfirmed = 'true';
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
    delete form.dataset.presaleConfirmed;
  };
}

if (!customElements.get('soleil-presale')) {
  customElements.define('soleil-presale', SoleilPresale);
}
